/**
 * Bill Utility Functions
 * 
 * Handles retrieval of student bills with priority:
 * 1. Individual student bill (studentBillingdata)
 * 2. Class-level bill (class.billingdata)
 * 3. Default class fees (class.classfees)
 */

/**
 * Get the appropriate bill for a student for a given term
 * @param {Object} student - Student object
 * @param {Object} classData - Class data object
 * @param {String} term - Term identifier (e.g., "1st Term 2024 / 2025")
 * @returns {Object} Bill data with structure: { totalbill, bill, billnotes, source }
 */
export function getStudentBill(student, classData, term) {
  if (!student || !classData || !term) {
    return null;
  }

  // Priority 1: Check for individual student bill
  if (student.studentBillingdata && Array.isArray(student.studentBillingdata)) {
    const individualBill = student.studentBillingdata.find(
      bill => bill.term === term
    );
    
    if (individualBill) {
      return {
        totalbill: parseFloat(individualBill.bill?.reduce((sum, charge) => 
          sum + (parseFloat(charge.amount) || 0), 0) || 0),
        bill: individualBill.bill || [],
        billnotes: individualBill.billnotes || [],
        source: 'individual',
        term: individualBill.term
      };
    }
  }

  // Priority 2: Check for class-level bill
  if (classData.billingdata && Array.isArray(classData.billingdata)) {
    const classBill = classData.billingdata.find(
      bill => bill.term === term
    );
    
    if (classBill) {
      return {
        totalbill: parseFloat(classBill.bill?.reduce((sum, charge) => 
          sum + (parseFloat(charge.amount) || 0), 0) || 0),
        bill: classBill.bill || [],
        billnotes: classBill.billnotes || [],
        source: 'class',
        term: classBill.term
      };
    }
  }

  // Priority 3: Use default class fees
  const classFees = parseFloat(classData.classfees) || 0;
  return {
    totalbill: classFees,
    bill: [],
    billnotes: [],
    source: 'default',
    term: term
  };
}

/**
 * Calculate total fees due for a student including bill charges
 * @param {Object} student - Student object
 * @param {Object} classData - Class data object
 * @param {String} term - Term identifier
 * @returns {Number} Total fees due
 */
export function calculateTotalFeesDue(student, classData, term) {
  const bill = getStudentBill(student, classData, term);
  
  if (!bill) {
    return 0;
  }

  // Get arrears from finance record if available
  let arrears = 0;
  if (student.finance && student.finance.length > 0) {
    const latestFinance = student.finance[student.finance.length - 1];
    arrears = parseFloat(latestFinance.Arrears) || 0;
  }

  // Total = bill total + arrears
  return bill.totalbill + arrears;
}

/**
 * Get other charges from bill
 * @param {Object} student - Student object
 * @param {Object} classData - Class data object
 * @param {String} term - Term identifier
 * @returns {Number} Sum of other charges from bill
 */
export function getOtherChargesFromBill(student, classData, term) {
  const bill = getStudentBill(student, classData, term);
  
  if (!bill || !bill.bill || !Array.isArray(bill.bill)) {
    return 0;
  }

  // Sum all charges from the bill
  return bill.bill.reduce((sum, charge) => {
    return sum + (parseFloat(charge.amount) || 0);
  }, 0);
}

/**
 * Check if student has individual bill for a term
 * @param {Object} student - Student object
 * @param {String} term - Term identifier
 * @returns {Boolean} True if student has individual bill
 */
export function hasIndividualBill(student, term) {
  if (!student || !student.studentBillingdata || !Array.isArray(student.studentBillingdata)) {
    return false;
  }
  
  return student.studentBillingdata.some(bill => bill.term === term);
}

/**
 * Get bill source indicator
 * @param {Object} student - Student object
 * @param {Object} classData - Class data object
 * @param {String} term - Term identifier
 * @returns {String} 'Individual', 'Class', or 'Default'
 */
export function getBillSource(student, classData, term) {
  const bill = getStudentBill(student, classData, term);
  if (!bill) return 'Default';
  
  const sourceMap = {
    'individual': 'Individual',
    'class': 'Class',
    'default': 'Default'
  };
  
  return sourceMap[bill.source] || 'Default';
}
